import { Box, Grid } from '@material-ui/core'
import { Button } from 'components/facets/Button'
import { useCardData } from 'components/providers/CardDataProvider'
import { useUser } from 'components/providers/UserProvider'
import powertoolsLogo from 'img/blue-logo.svg'
import cardmarketLogoFull from 'img/cardmarketLogoFull.svg'
import { ReactComponent as CompareIcon } from 'img/compare.svg'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AggregatedInventoryArticle,
  convertToAggregatedStock,
  generateArticleKey,
  InventoryArticle,
  INVENTORY_STEPS,
  INVENTORY_TRANSACTION_SOURCES,
  isSingle,
  PTInventoryArticle,
} from 'shared'

interface SourceSelectionProps {
  incomingStock: InventoryArticle[]
  incomingAggregatedStock: AggregatedInventoryArticle[]
}

interface SourceSelectionBoxProps {
  source: string
  image?: JSX.Element
  img?: string
  counters?: {
    uniqueSingles: number
    singles: number
    uniqueSealed: number
    sealed: number
  }
  text: string
  subText?: string
  disabled?: boolean
  onClick?: () => void
}

const SourceSelectionBox = ({
  source,
  img,
  image,
  text,
  subText,
  disabled,
  counters,
  onClick,
}: SourceSelectionBoxProps) => {
  const { t } = useTranslation()

  return (
    <>
      <Box
        width="260px"
        height="305px"
        className={disabled ? 'inventoryBoxDisabled' : 'inventoryBox'}
      >
        <Box marginBottom={4}>{image ? image : <img src={img} height="50px" />}</Box>
        <Box flexGrow={1}>
          {counters ? (
            <>
              <div data-testid={`${source}-unique-singles`} className="bodyFont">
                {t('ptInventory.stockReview.uniqueSingles')}: {counters.uniqueSingles}
              </div>
              <div data-testid={`${source}-singles`} className="bodyFont">
                {t('ptInventory.stockReview.singles')}: {counters.singles}
              </div>
              <div data-testid={`${source}-unique-accessories`} className="bodyFont">
                {t('ptInventory.stockReview.uniqueAccessories')}: {counters.uniqueSealed}
              </div>
              <div data-testid={`${source}-accessories`} className="bodyFont">
                {t('ptInventory.stockReview.accessories')}: {counters.sealed}
              </div>
            </>
          ) : (
            <div className="bodyFont">{subText}</div>
          )}
        </Box>
        <Button
          variant="contained"
          data-testid={`${source}-btn`}
          disabled={disabled}
          onClick={onClick}
        >
          {text}
        </Button>
      </Box>
    </>
  )
}

export const SourceSelection = ({
  incomingStock,
  incomingAggregatedStock,
}: SourceSelectionProps): JSX.Element => {
  const { t } = useTranslation()

  const { user } = useUser()
  const cardData = useCardData()
  const setStep = useInventoryStore((state) => state.setStep)
  const ptArticles = useInventoryStore((state) => state.ptArticles)
  // const diffSelectedArticles = useInventoryStore((state) => state.diffSelectedArticles)
  const setDiffSelectedArticles = useInventoryStore((state) => state.setDiffSelectedArticles)
  // const matchingData = useInventoryStore((state) => state.matchingData)
  const numConflicts = useInventoryStore((state) => state.numConflicts)
  const paddedLeftData = useInventoryStore((state) => state.paddedLeftData)
  const paddedRightData = useInventoryStore((state) => state.paddedRightData)
  const setSelectedSource = useInventoryStore((state) => state.setSelectedSource)

  const [disabledBoxes, setDisabledBoxes] = useState<string[]>([])
  const [ptCounters, setPtCounters] = useState<{
    uniqueSingles: number
    singles: number
    uniqueSealed: number
    sealed: number
  }>({
    uniqueSingles: 0,
    singles: 0,
    uniqueSealed: 0,
    sealed: 0,
  })
  const [mkmCounters, setMkmCounters] = useState<{
    uniqueSingles: number
    singles: number
    uniqueSealed: number
    sealed: number
  }>({
    uniqueSingles: 0,
    singles: 0,
    uniqueSealed: 0,
    sealed: 0,
  })

  const getMkmCounters = (stock: InventoryArticle[]) => {
    let uniqueSingles = 0
    let singles = 0
    let uniqueSealed = 0
    let sealed = 0

    // Cardmarket has one listing per price and comment: listings of the same product count once, as the
    // product they become on Powertools
    const singleKeys = new Set<string>()
    const sealedKeys = new Set<string>()
    stock.forEach((article) => {
      if (isSingle(article.card)) {
        singleKeys.add(generateArticleKey(article))
        singles += article.quantity
      } else {
        sealedKeys.add(generateArticleKey(article))
        sealed += article.quantity
      }
    })
    uniqueSingles = singleKeys.size
    uniqueSealed = sealedKeys.size

    return {
      uniqueSingles,
      singles,
      uniqueSealed,
      sealed,
    }
  }

  const getPtCounters = (stock: PTInventoryArticle[]) => {
    let uniqueSingles = 0
    let singles = 0
    let uniqueSealed = 0
    let sealed = 0

    const isSingle = (categoryName: string) => {
      return categoryName.includes('Single')
    }

    // The stock comes one row per location: add the locations up per article, then leave out what open
    // orders reserved (never below zero). Counted as on the Stock page and the dashboard: an article
    // counts as long as it has copies, even if all of them are reserved
    const quantityByArticle = new Map<number, { article: PTInventoryArticle; quantity: number }>()
    stock.forEach((article) => {
      const entry = quantityByArticle.get(article._id)
      if (entry) entry.quantity += article.locations[0].quantity
      else quantityByArticle.set(article._id, { article, quantity: article.locations[0].quantity })
    })

    quantityByArticle.forEach(({ article, quantity }) => {
      const available = Math.max(quantity - article.reservedQuantity, 0)
      if (isSingle(article.ptProduct.mkmProduct?.categoryName ?? '')) {
        uniqueSingles++
        singles += available
      } else {
        uniqueSealed++
        sealed += available
      }
    })

    return {
      uniqueSingles,
      singles,
      uniqueSealed,
      sealed,
    }
  }

  useEffect(() => {
    console.log(`disabling sources`)
    const newDisabledBoxes = []
    if (user?.featureFlags?.forceGradeSync) {
      // Graded-stock cleanup: the user was force-redirected here to reconcile duplicated grades.
      // Take away the bulk shortcuts (all from PowerTools / all from Cardmarket) and make them go
      // through the card-by-card compare, so they can't blindly overwrite one source with the other.
      newDisabledBoxes.push('pt')
      newDisabledBoxes.push('mkm')
    } else if (ptArticles.length === 0) {
      newDisabledBoxes.push('pt')
      newDisabledBoxes.push('diff')
      handleImportAllCardmarket()
    } else if (numConflicts === 0 && paddedLeftData.length === 0 && paddedRightData.length === 0) {
      newDisabledBoxes.push('diff')
      handleImportAllCardmarket()
    }
    setDisabledBoxes(newDisabledBoxes)

    const newPtCounters = getPtCounters(ptArticles)
    setPtCounters(newPtCounters)
  }, [ptArticles])

  useEffect(() => {
    const newMkmCounters = getMkmCounters(incomingStock)
    setMkmCounters(newMkmCounters)
  }, [])

  const handleImportAllCardmarket = async () => {
    console.log(`handleImportAllCardmarket`)
    setSelectedSource(INVENTORY_TRANSACTION_SOURCES.CARDMARKET)
    setDiffSelectedArticles(incomingAggregatedStock)
    setStep(INVENTORY_STEPS.PRICE_SELECTION.idx)
  }

  const handleImportAllPowertools = async () => {
    console.log(`handleImportAllPowertools`)
    setSelectedSource(INVENTORY_TRANSACTION_SOURCES.POWERTOOLS)
    const aggregatedStock = convertToAggregatedStock(cardData, ptArticles)
    setDiffSelectedArticles(aggregatedStock)
    setStep(INVENTORY_STEPS.PRICE_SELECTION.idx)
  }

  const handleCompare = async () => {
    setStep(INVENTORY_STEPS.SOLVE_CONFLICTS.idx)
  }

  const isBoxDisabled = (box: string) => {
    return disabledBoxes.includes(box)
  }

  return (
    <>
      <Box>
        <Box className="title" marginBottom={5}>
          <div className="titleFont">{t('ptInventory.sourceSelection.title')}</div>
        </Box>
        <Box marginBottom={5}>
          <div className="bodyFont">{t('ptInventory.sourceSelection.text1')}</div>
        </Box>
        <Box marginBottom={2}>
          <div className="bodyFont">{t('ptInventory.sourceSelection.text2')}</div>
          <div className="bodyFont">{t('ptInventory.sourceSelection.text3')}</div>
          <div className="bodyFont">{t('ptInventory.sourceSelection.text4')}</div>
          <div className="bodyFont">{t('ptInventory.sourceSelection.text5')}</div>
        </Box>
        <Grid container>
          <Grid item data-testid="keep-all-from-powertools" xs={12} sm={12} md={6} lg={4} xl={3}>
            <SourceSelectionBox
              source="pt"
              img={powertoolsLogo}
              counters={ptCounters}
              text={t('ptInventory.sourceSelection.allPowertools')}
              disabled={isBoxDisabled('pt')}
              onClick={handleImportAllPowertools}
            />
          </Grid>
          <Grid item data-testid="keep-all-from-cardmarket" xs={12} sm={12} md={6} lg={4} xl={3}>
            <SourceSelectionBox
              source="mkm"
              img={cardmarketLogoFull}
              counters={mkmCounters}
              text={t('ptInventory.sourceSelection.allCardmarket')}
              disabled={isBoxDisabled('mkm')}
              onClick={handleImportAllCardmarket}
            />
          </Grid>
          <Grid item data-testid="decide-card-by-card" xs={12} sm={12} md={6} lg={4} xl={3}>
            <SourceSelectionBox
              source="diff"
              image={<CompareIcon />}
              text={t('ptInventory.sourceSelection.diff')}
              subText={t('ptInventory.sourceSelection.diffSubText')}
              disabled={isBoxDisabled('diff')}
              onClick={handleCompare}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
